/**
 * FairPersonasAdmin (DC-1605)
 *
 * Settings/admin surface for the per-organization `personas` table — the
 * Pistoia FAIR Maturity Matrix persona archetypes that drive the Triangulated
 * FAIR Assessment's Perceived leg (DC-1572), the persona target for group-
 * transcript speaker mapping (DC-1603), and the per-FAIR-pillar benefit
 * narrative injected into Bridge AI recommendation prompts (DC-1607).
 *
 * DC-1648: this is the ONLY persona surface. The legacy "Personas" tab
 * (GovernanceTopology over the retired ref_pistoia_personas table) is gone;
 * its force-directed assignment graph lives on here behind the
 * Catalog | Topology toggle (?view=topology → PersonaTopologyView), sharing
 * this file's PersonaDetailPanel via a render prop.
 *
 * Mounted under the Team Management settings layout at
 * `/settings/team/personas` (DC-1648 follow-up — Kathleen 6/10: "one place
 * for team + persona"; old /settings/governance/fair-personas URLs
 * redirect). CRUD against `/api/personas/*` (route layer in
 * `server/routes/triangulationRoutes.ts`).
 *
 * Layout — list of persona cards (name + archetype + source badge + synonym
 * chip). Click → detail panel with Value Proposition Canvas (persona_tasks /
 * issues / latent_gains), per-pillar benefits table, related-persona graph
 * chips. Create modal for custom personas (basic fields only — name +
 * archetype + description; rich Pistoia fields are seeder-owned and the
 * route schema does not accept them on create/update). Edit modal mirrors
 * create. Source-aware delete:
 *   - Pistoia rows: "Remove from this organization — re-seed to restore"
 *   - custom rows:  "Delete permanently"
 * Both call `DELETE /api/personas/:id` (no diverging endpoint); the copy
 * sets honest expectations about reversibility.
 *
 * Spec: DC-1606 schema + DC-1605 scope expansion comment.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import type { Persona, PersonaSource } from '../../types/triangulation';
import { PersonaTopologyView } from './fair-personas/PersonaTopologyView';

// ---------------------------------------------------------------------------
// Constants & helpers
// ---------------------------------------------------------------------------

const NAME_MAX = 255;
const ARCHETYPE_MAX = 255;
const DESC_MAX = 256;

// FAIR canonical pillars in canonical order. Sub-pillar keys in
// `pillar_benefits` are grouped by first-letter match against this set.
const PILLAR_GROUPS: ReadonlyArray<{ letter: string; label: string }> = [
  { letter: 'F', label: 'Findable' },
  { letter: 'A', label: 'Accessible' },
  { letter: 'I', label: 'Interoperable' },
  { letter: 'R', label: 'Reusable' },
];

interface FormErrors {
  name?: string;
  archetype?: string;
  description?: string;
  submit?: string;
}

function validatePersonaForm(values: {
  name: string;
  archetype: string;
  description: string;
}): FormErrors {
  const errs: FormErrors = {};
  if (!values.name.trim()) errs.name = 'Name is required';
  else if (values.name.length > NAME_MAX) errs.name = `Name must be ${NAME_MAX} characters or fewer`;
  if (values.archetype.length > ARCHETYPE_MAX) {
    errs.archetype = `Archetype must be ${ARCHETYPE_MAX} characters or fewer`;
  }
  if (values.description.length > DESC_MAX) {
    errs.description = `Description must be ${DESC_MAX} characters or fewer`;
  }
  return errs;
}

/**
 * Group `pillar_benefits` keys by canonical FAIR pillar (first-letter match).
 * Sub-pillar keys within each group are sorted lexicographically for
 * deterministic rendering. Returns an array preserving canonical pillar
 * order; empty groups are omitted.
 */
function groupPillarBenefits(
  benefits: Record<string, string> | null,
): Array<{ pillar: string; entries: Array<{ key: string; text: string }> }> {
  if (!benefits) return [];
  const groups: Array<{ pillar: string; entries: Array<{ key: string; text: string }> }> = [];
  for (const { letter, label } of PILLAR_GROUPS) {
    const matchingKeys = Object.keys(benefits)
      .filter(k => k.length > 0 && k.charAt(0).toUpperCase() === letter)
      .filter(k => typeof benefits[k] === 'string' && benefits[k].trim().length > 0)
      .sort();
    if (matchingKeys.length === 0) continue;
    groups.push({
      pillar: label,
      entries: matchingKeys.map(k => ({ key: k, text: benefits[k] })),
    });
  }
  return groups;
}

function personaSourceOf(p: Persona): PersonaSource | 'unknown' {
  return p.source ?? 'unknown';
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface PersonaCardProps {
  persona: Persona;
  selected: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

function PersonaCard({ persona, selected, onSelect, onEdit, onDelete }: PersonaCardProps) {
  const source = personaSourceOf(persona);
  const synonymCount = persona.synonyms?.length ?? 0;
  const benefitCount = persona.pillar_benefits ? Object.keys(persona.pillar_benefits).length : 0;
  return (
    <div
      data-testid={`fair-persona-card-${persona.id}`}
      onClick={onSelect}
      style={{
        background: selected ? '#f0fdfa' : '#ffffff',
        border: `1px solid ${selected ? '#0F6E73' : '#e5e7eb'}`,
        borderRadius: '0.5rem',
        padding: '1rem 1.25rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        cursor: 'pointer',
        transition: 'border-color 0.15s ease, background 0.15s ease',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '0.75rem',
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <h3
            style={{
              margin: 0,
              fontSize: '1rem',
              fontWeight: 600,
              color: '#111827',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {persona.name}
          </h3>
          {persona.archetype && (
            <p
              style={{
                margin: '0.125rem 0 0 0',
                fontSize: '0.75rem',
                color: '#6b7280',
                fontStyle: 'italic',
              }}
            >
              {persona.archetype}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.375rem', flexShrink: 0 }}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            data-testid={`edit-fair-persona-${persona.id}`}
            style={buttonStyle('secondary')}
          >
            Edit
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            data-testid={`delete-fair-persona-${persona.id}`}
            style={buttonStyle('danger-outline')}
          >
            Delete
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
        <span style={sourceBadgeStyle(source)} title={`Source: ${source}`}>
          {source === 'pistoia' ? 'Pistoia' : source === 'custom' ? 'Custom' : source === 'compass' ? 'Compass' : 'Legacy'}
        </span>
        {benefitCount > 0 && (
          <span
            style={countBadgeStyle()}
            title={`${benefitCount} FAIR sub-pillar benefit entries`}
          >
            {benefitCount} pillar benefits
          </span>
        )}
        {synonymCount > 0 && (
          <span style={countBadgeStyle()} title={`${synonymCount} synonyms`}>
            {synonymCount} synonyms
          </span>
        )}
      </div>
    </div>
  );
}

interface PersonaDetailPanelProps {
  persona: Persona;
}

function PersonaDetailPanel({ persona }: PersonaDetailPanelProps) {
  const pillarGroups = useMemo(
    () => groupPillarBenefits(persona.pillar_benefits),
    [persona.pillar_benefits],
  );
  const source = personaSourceOf(persona);

  return (
    <div
      data-testid={`fair-persona-detail-${persona.id}`}
      style={{
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '0.5rem',
        padding: '1.25rem 1.5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
      }}
    >
      <header>
        <h2
          style={{
            margin: 0,
            fontSize: '1.25rem',
            fontWeight: 700,
            color: '#111827',
          }}
        >
          {persona.name}
        </h2>
        {persona.archetype && (
          <p
            style={{
              margin: '0.125rem 0 0 0',
              fontSize: '0.875rem',
              color: '#4b5563',
              fontStyle: 'italic',
            }}
          >
            {persona.archetype}
          </p>
        )}
        <div style={{ display: 'flex', gap: '0.375rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
          <span style={sourceBadgeStyle(source)}>
            {source === 'pistoia' ? 'Pistoia FAIR Maturity Matrix' : source === 'custom' ? 'Custom (consultant-authored)' : source === 'compass' ? 'Compass-curated (governance role)' : 'Legacy (pre-DC-1606)'}
          </span>
          {persona.source_catalog_version && (
            <span style={countBadgeStyle()}>{persona.source_catalog_version}</span>
          )}
        </div>
      </header>

      {persona.fair_benefits_description && (
        <section>
          <h3 style={sectionHeadingStyle}>FAIR benefits — overview</h3>
          <p style={proseStyle}>{persona.fair_benefits_description}</p>
        </section>
      )}

      {(persona.persona_tasks || persona.issues || persona.latent_gains) && (
        <section data-testid="fair-persona-vpc">
          <h3 style={sectionHeadingStyle}>Value Proposition Canvas</h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '0.75rem',
            }}
          >
            <VpcCell title="Tasks" body={persona.persona_tasks} />
            <VpcCell title="Issues / pains" body={persona.issues} />
            <VpcCell title="Latent gains" body={persona.latent_gains} />
          </div>
        </section>
      )}

      {pillarGroups.length > 0 && (
        <section data-testid="fair-persona-pillar-benefits">
          <h3 style={sectionHeadingStyle}>Per-pillar benefits</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {pillarGroups.map(group => (
              <div key={group.pillar}>
                <h4
                  style={{
                    margin: '0 0 0.25rem 0',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    color: '#0F6E73',
                    fontFamily: "'JetBrains Mono', Consolas, monospace",
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}
                >
                  {group.pillar}
                </h4>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '0.8125rem',
                  }}
                >
                  <tbody>
                    {group.entries.map(({ key, text }) => (
                      <tr key={key}>
                        <td
                          style={{
                            padding: '0.375rem 0.5rem 0.375rem 0',
                            verticalAlign: 'top',
                            width: '4rem',
                            fontFamily: "'JetBrains Mono', Consolas, monospace",
                            fontWeight: 600,
                            color: '#0F6E73',
                          }}
                        >
                          {key}
                        </td>
                        <td
                          style={{
                            padding: '0.375rem 0',
                            verticalAlign: 'top',
                            color: '#374151',
                            lineHeight: 1.5,
                          }}
                        >
                          {text}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </section>
      )}

      {persona.synonyms && persona.synonyms.length > 0 && (
        <section>
          <h3 style={sectionHeadingStyle}>Synonyms</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
            {persona.synonyms.map(s => (
              <span key={s} style={chipStyle()}>{s}</span>
            ))}
          </div>
        </section>
      )}

      {persona.related_personas && persona.related_personas.length > 0 && (
        <section data-testid="fair-persona-related">
          <h3 style={sectionHeadingStyle}>Related personas</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
            {persona.related_personas.map((r, idx) => (
              <span key={`${r.slug}-${idx}`} style={chipStyle()} title={r.relation ?? undefined}>
                {r.slug}
                {r.relation && (
                  <span style={{ color: '#6b7280', marginLeft: '0.25rem' }}>· {r.relation}</span>
                )}
              </span>
            ))}
          </div>
        </section>
      )}

      {persona.full_description && (
        <section>
          <h3 style={sectionHeadingStyle}>Full description</h3>
          <p style={proseStyle}>{persona.full_description}</p>
        </section>
      )}

      {!persona.pillar_benefits && !persona.persona_tasks && !persona.fair_benefits_description && (
        <p style={{ ...proseStyle, fontStyle: 'italic', color: '#6b7280' }}>
          No rich Pistoia data on this persona. Custom personas authored via this UI carry only
          name, archetype, and description until a Pistoia catalog re-seed populates the
          remaining fields (or until rich-field editing ships in a later release).
        </p>
      )}
    </div>
  );
}

function VpcCell({ title, body }: { title: string; body: string | null }) {
  if (!body) return null;
  return (
    <div
      style={{
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '0.375rem',
        padding: '0.75rem 0.875rem',
      }}
    >
      <h4
        style={{
          margin: '0 0 0.375rem 0',
          fontSize: '0.75rem',
          fontWeight: 600,
          color: '#0F6E73',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {title}
      </h4>
      <p style={{ margin: 0, fontSize: '0.8125rem', color: '#374151', lineHeight: 1.5 }}>
        {body}
      </p>
    </div>
  );
}

interface PersonaFormModalProps {
  mode: 'create' | 'edit';
  initial: Persona | null;
  onClose: () => void;
  onSubmit: (values: {
    name: string;
    archetype: string | null;
    description: string | null;
  }) => Promise<void>;
}

function PersonaFormModal({ mode, initial, onClose, onSubmit }: PersonaFormModalProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [archetype, setArchetype] = useState(initial?.archetype ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);

  const isPistoia = initial?.source === 'pistoia';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = {
      name: name.trim(),
      archetype: archetype.trim(),
      description: description.trim(),
    };
    const validation = validatePersonaForm(trimmed);
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      await onSubmit({
        name: trimmed.name,
        archetype: trimmed.archetype.length > 0 ? trimmed.archetype : null,
        description: trimmed.description.length > 0 ? trimmed.description : null,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrors({
        submit: msg.toLowerCase().includes('409') || msg.toLowerCase().includes('conflict')
          ? 'A persona with that name already exists in this organization.'
          : msg,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="fair-persona-form-title"
      style={modalBackdropStyle}
      data-testid="fair-persona-form-modal"
    >
      <div style={modalCardStyle}>
        <h2
          id="fair-persona-form-title"
          style={{ margin: '0 0 0.25rem 0', fontSize: '1.125rem', fontWeight: 600, color: '#111827' }}
        >
          {mode === 'create' ? 'Create custom persona' : `Edit ${initial?.name ?? 'persona'}`}
        </h2>
        {isPistoia && (
          <p
            style={{
              margin: '0 0 0.75rem 0',
              fontSize: '0.8125rem',
              color: '#6b7280',
              lineHeight: 1.5,
            }}
          >
            This is a Pistoia FAIR Maturity Matrix persona. You can edit the basic display
            fields below; rich fields (VPC, per-pillar benefits, synonyms, related personas)
            are seeder-owned and update when the Pistoia catalog is re-seeded.
          </p>
        )}
        <form onSubmit={handleSubmit} noValidate>
          <FieldLabel htmlFor="fair-persona-name">Name</FieldLabel>
          <input
            id="fair-persona-name"
            data-testid="fair-persona-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={NAME_MAX}
            disabled={submitting}
            style={inputStyle(Boolean(errors.name))}
          />
          {errors.name && <FieldError>{errors.name}</FieldError>}

          <FieldLabel htmlFor="fair-persona-archetype">Archetype</FieldLabel>
          <input
            id="fair-persona-archetype"
            data-testid="fair-persona-archetype-input"
            value={archetype}
            onChange={(e) => setArchetype(e.target.value)}
            maxLength={ARCHETYPE_MAX}
            disabled={submitting}
            placeholder="e.g. Operations, Governance, Engineering"
            style={inputStyle(Boolean(errors.archetype))}
          />
          <p style={helpTextStyle}>Optional analytic label. Used in cards and prompt context.</p>
          {errors.archetype && <FieldError>{errors.archetype}</FieldError>}

          <FieldLabel htmlFor="fair-persona-description">Description</FieldLabel>
          <textarea
            id="fair-persona-description"
            data-testid="fair-persona-description-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={DESC_MAX}
            disabled={submitting}
            rows={3}
            style={inputStyle(Boolean(errors.description))}
          />
          <p style={helpTextStyle}>
            Optional condensed summary. {DESC_MAX - description.length} characters remaining.
          </p>
          {errors.description && <FieldError>{errors.description}</FieldError>}

          {errors.submit && (
            <div
              role="alert"
              data-testid="fair-persona-form-submit-error"
              style={{
                marginTop: '0.75rem',
                padding: '0.5rem 0.75rem',
                background: '#fef2f2',
                border: '1px solid #fca5a5',
                borderRadius: '0.375rem',
                color: '#991b1b',
                fontSize: '0.8125rem',
              }}
            >
              {errors.submit}
            </div>
          )}

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.5rem',
              marginTop: '1.25rem',
            }}
          >
            <button type="button" onClick={onClose} disabled={submitting} style={buttonStyle('secondary')}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="fair-persona-form-submit"
              style={buttonStyle('primary')}
            >
              {submitting ? 'Saving...' : mode === 'create' ? 'Create persona' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface DeleteConfirmModalProps {
  persona: Persona;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}

function DeleteConfirmModal({ persona, onCancel, onConfirm }: DeleteConfirmModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  // DC-1648: 'compass' personas (Document_Owner) are re-seedable like
  // Pistoia rows — same remove-vs-delete framing.
  const isReseedable = persona.source === 'pistoia' || persona.source === 'compass';
  const sourceLabel = persona.source === 'compass'
    ? 'Compass-curated governance'
    : 'Pistoia FAIR Maturity Matrix';
  const title = isReseedable
    ? `Remove ${persona.name} from this organization?`
    : `Delete ${persona.name}?`;
  const body = isReseedable
    ? `This is a ${sourceLabel} persona. Removing it deletes the row from this organization, but the canonical catalog entry is preserved. Re-run the Pistoia seeder (deploy/seed-personas.sh) to restore the persona with current rich fields. Any interviews currently mapped to this persona will have their persona_id set to NULL — they will continue to bucket via interviewee_role only. If the persona has active governance assignments, removal is blocked until they are unassigned.`
    : `This is a custom persona authored in this organization. Deletion is permanent — there is no canonical source to re-seed from. Any interviews currently mapped to this persona will have their persona_id set to NULL. If the persona has active governance assignments, deletion is blocked until they are unassigned.`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="fair-persona-delete-title"
      style={modalBackdropStyle}
      data-testid="fair-persona-delete-modal"
    >
      <div style={modalCardStyle}>
        <h2
          id="fair-persona-delete-title"
          style={{ margin: '0 0 0.75rem 0', fontSize: '1.125rem', fontWeight: 600, color: '#111827' }}
        >
          {title}
        </h2>
        <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.875rem', color: '#374151', lineHeight: 1.5 }}>
          {body}
        </p>
        {error && (
          <div
            role="alert"
            style={{
              marginBottom: '0.75rem',
              padding: '0.5rem 0.75rem',
              background: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: '0.375rem',
              color: '#991b1b',
              fontSize: '0.8125rem',
            }}
          >
            {error}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button type="button" onClick={onCancel} disabled={submitting} style={buttonStyle('secondary')}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            data-testid="fair-persona-delete-confirm"
            style={buttonStyle('danger')}
          >
            {submitting ? 'Removing...' : isReseedable ? 'Remove from organization' : 'Delete permanently'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const FairPersonasAdmin: React.FC = () => {
  const { get, post, patch, delete: del, invalidateCache } = useApiClient();

  // DC-1648: Catalog | Topology view toggle, URL-driven for deep links
  // (?view=topology). Catalog is the default — anything else falls back.
  const [searchParams, setSearchParams] = useSearchParams();
  const view: 'catalog' | 'topology' =
    searchParams.get('view') === 'topology' ? 'topology' : 'catalog';
  const setView = useCallback(
    (next: 'catalog' | 'topology') => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === 'topology') params.set('view', 'topology');
          else params.delete('view');
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const [personas, setPersonas] = useState<Persona[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState<'all' | PersonaSource>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [formMode, setFormMode] = useState<'create' | 'edit' | null>(null);
  const [editTarget, setEditTarget] = useState<Persona | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Persona | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      invalidateCache('/api/personas');
      const list = await get<Persona[]>('/api/personas');
      setPersonas(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [get, invalidateCache]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return personas.filter(p => {
      // Legacy rows (source === null) are a distinct state (rendered as their own
      // badge); they must NOT be coerced into 'custom' here or the "Custom only"
      // filter mislabels them. A null source matches only the "All sources" view.
      if (sourceFilter !== 'all' && p.source !== sourceFilter) return false;
      if (!q) return true;
      const nameMatch = p.name.toLowerCase().includes(q);
      const archetypeMatch = p.archetype?.toLowerCase().includes(q) ?? false;
      const synonymMatch = p.synonyms?.some(s => s.toLowerCase().includes(q)) ?? false;
      return nameMatch || archetypeMatch || synonymMatch;
    });
  }, [personas, query, sourceFilter]);

  const selected = useMemo(
    () => filtered.find(p => p.id === selectedId) ?? null,
    [filtered, selectedId],
  );

  const handleCreate = useCallback(
    async (values: { name: string; archetype: string | null; description: string | null }) => {
      await post('/api/personas', values);
      setFormMode(null);
      setEditTarget(null);
      await refresh();
    },
    [post, refresh],
  );

  const handleUpdate = useCallback(
    async (values: { name: string; archetype: string | null; description: string | null }) => {
      if (!editTarget) return;
      await patch(`/api/personas/${editTarget.id}`, values);
      setFormMode(null);
      setEditTarget(null);
      await refresh();
    },
    [editTarget, patch, refresh],
  );

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    await del(`/api/personas/${deleteTarget.id}`);
    if (selectedId === deleteTarget.id) setSelectedId(null);
    setDeleteTarget(null);
    await refresh();
  }, [del, deleteTarget, refresh, selectedId]);

  return (
    <div data-testid="fair-personas-admin" style={{ padding: '1.5rem 1.75rem', maxWidth: 1280 }}>
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '1rem',
          gap: '1rem',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>
            FAIR Personas
          </h1>
          <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#6b7280', lineHeight: 1.5 }}>
            Pistoia FAIR Maturity Matrix persona archetypes — the single persona system behind
            interview and speaker mapping, the Triangulated FAIR Assessment&apos;s Perceived leg,
            governance role assignments, and the Bridge AI recommendation prompt.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexShrink: 0 }}>
          {/* DC-1648: Catalog | Topology segmented toggle */}
          <div
            role="tablist"
            aria-label="Persona view"
            data-testid="fair-personas-view-toggle"
            style={{
              display: 'inline-flex',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              overflow: 'hidden',
            }}
          >
            <button
              type="button"
              role="tab"
              aria-selected={view === 'catalog'}
              data-testid="fair-personas-view-catalog"
              onClick={() => setView('catalog')}
              style={{
                ...segmentStyle(view === 'catalog'),
                borderRight: '1px solid #d1d5db',
              }}
            >
              Catalog
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'topology'}
              data-testid="fair-personas-view-topology"
              onClick={() => setView('topology')}
              style={segmentStyle(view === 'topology')}
            >
              Topology
            </button>
          </div>
          {view === 'catalog' && (
            <button
              type="button"
              onClick={() => {
                setEditTarget(null);
                setFormMode('create');
              }}
              data-testid="create-fair-persona-button"
              style={buttonStyle('primary')}
            >
              + Create custom persona
            </button>
          )}
        </div>
      </header>

      {view === 'topology' && (
        <PersonaTopologyView
          personas={personas}
          loading={loading}
          error={error}
          onRetry={() => void refresh()}
          onGoToCatalog={() => setView('catalog')}
          renderPersonaDetail={(p) => <PersonaDetailPanel persona={p} />}
        />
      )}

      {view === 'catalog' && (
      <>


      <div
        style={{
          display: 'flex',
          gap: '0.75rem',
          marginBottom: '1rem',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <input
          type="search"
          placeholder="Filter by name, archetype, or synonym…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="fair-personas-search"
          style={{ ...inputStyle(false), maxWidth: 320 }}
        />
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value as 'all' | PersonaSource)}
          data-testid="fair-personas-source-filter"
          style={{
            ...inputStyle(false),
            maxWidth: 200,
            cursor: 'pointer',
          }}
        >
          <option value="all">All sources</option>
          <option value="pistoia">Pistoia only</option>
          <option value="custom">Custom only</option>
          <option value="compass">Compass-curated only</option>
        </select>
        <span
          style={{
            marginLeft: 'auto',
            fontSize: '0.8125rem',
            color: '#6b7280',
            fontFamily: "'JetBrains Mono', Consolas, monospace",
          }}
        >
          {filtered.length} of {personas.length}
        </span>
      </div>

      {loading && (
        <div
          data-testid="fair-personas-loading"
          style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}
        >
          Loading personas…
        </div>
      )}

      {!loading && error && (
        <div
          data-testid="fair-personas-error"
          role="alert"
          style={{
            padding: '1rem 1.25rem',
            background: '#fef2f2',
            border: '1px solid #fca5a5',
            borderRadius: '0.5rem',
            color: '#991b1b',
          }}
        >
          <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem' }}>
            Failed to load personas: {error}
          </p>
          <button type="button" onClick={() => void refresh()} style={buttonStyle('secondary')}>
            Retry
          </button>
        </div>
      )}

      {!loading && !error && personas.length === 0 && (
        <div
          data-testid="fair-personas-empty"
          style={{
            padding: '2.5rem 1.5rem',
            textAlign: 'center',
            background: '#f9fafb',
            border: '1px dashed #d1d5db',
            borderRadius: '0.5rem',
            color: '#6b7280',
          }}
        >
          <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9375rem', color: '#374151' }}>
            No FAIR personas seeded
          </p>
          <p style={{ margin: 0, fontSize: '0.8125rem' }}>
            Run <code>deploy/seed-personas.sh</code> to seed the 30 Pistoia FAIR Maturity Matrix
            archetypes, or create a custom persona above.
          </p>
        </div>
      )}

      {!loading && !error && personas.length > 0 && filtered.length === 0 && (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280', fontSize: '0.875rem' }}>
          No personas match the current filter.
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div
          data-testid="fair-personas-list-and-detail"
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(320px, 1fr) minmax(420px, 2fr)',
            gap: '1rem',
            alignItems: 'start',
          }}
        >
          <div
            data-testid="fair-personas-list"
            style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}
          >
            {filtered.map(p => (
              <PersonaCard
                key={p.id}
                persona={p}
                selected={p.id === selectedId}
                onSelect={() => setSelectedId(p.id)}
                onEdit={() => {
                  setEditTarget(p);
                  setFormMode('edit');
                }}
                onDelete={() => setDeleteTarget(p)}
              />
            ))}
          </div>
          {selected ? (
            <PersonaDetailPanel persona={selected} />
          ) : (
            <div
              data-testid="fair-personas-detail-placeholder"
              style={{
                background: '#f9fafb',
                border: '1px dashed #d1d5db',
                borderRadius: '0.5rem',
                padding: '2rem',
                textAlign: 'center',
                color: '#6b7280',
                fontSize: '0.875rem',
              }}
            >
              Select a persona to see its Value Proposition Canvas and per-pillar FAIR benefits.
            </div>
          )}
        </div>
      )}

      </>
      )}

      {formMode && (
        <PersonaFormModal
          mode={formMode}
          initial={editTarget}
          onClose={() => {
            setFormMode(null);
            setEditTarget(null);
          }}
          onSubmit={formMode === 'create' ? handleCreate : handleUpdate}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          persona={deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
};

export default FairPersonasAdmin;

// ---------------------------------------------------------------------------
// Style helpers (inline; mirrors DomainsAdmin.tsx — DC-1493)
// ---------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'danger-outline';

function buttonStyle(variant: ButtonVariant): React.CSSProperties {
  const base: React.CSSProperties = {
    padding: '0.4375rem 0.875rem',
    fontSize: '0.8125rem',
    fontWeight: 600,
    borderRadius: '0.375rem',
    cursor: 'pointer',
    border: '1px solid transparent',
    transition: 'background 0.15s ease, color 0.15s ease, border-color 0.15s ease',
  };
  if (variant === 'primary') return { ...base, background: '#0F6E73', color: '#ffffff' };
  if (variant === 'secondary') return { ...base, background: '#ffffff', color: '#374151', borderColor: '#d1d5db' };
  if (variant === 'danger') return { ...base, background: '#dc2626', color: '#ffffff' };
  return { ...base, background: '#ffffff', color: '#b91c1c', borderColor: '#fca5a5' };
}

/** DC-1648: Catalog | Topology segmented-toggle button styling. */
function segmentStyle(active: boolean): React.CSSProperties {
  return {
    padding: '0.4375rem 0.875rem',
    fontSize: '0.8125rem',
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    background: active ? '#0F6E73' : '#ffffff',
    color: active ? '#ffffff' : '#374151',
  };
}

function inputStyle(invalid: boolean): React.CSSProperties {
  return {
    width: '100%',
    padding: '0.5rem 0.75rem',
    fontSize: '0.875rem',
    border: `1px solid ${invalid ? '#dc2626' : '#d1d5db'}`,
    borderRadius: '0.375rem',
    background: '#ffffff',
    color: '#111827',
    boxSizing: 'border-box',
  };
}

function sourceBadgeStyle(source: PersonaSource | 'unknown'): React.CSSProperties {
  const colors: Record<PersonaSource | 'unknown', { bg: string; border: string; fg: string }> = {
    pistoia: { bg: 'rgba(15, 110, 115, 0.08)', border: '#0F6E73', fg: '#0F6E73' },
    custom: { bg: 'rgba(154, 95, 0, 0.08)', border: '#9A5F00', fg: '#9A5F00' },
    // DC-1648: Compass-curated personas (Document_Owner) — slate, distinct
    // from both the Pistoia teal and the custom amber.
    compass: { bg: 'rgba(55, 65, 81, 0.08)', border: '#374151', fg: '#374151' },
    unknown: { bg: '#f3f4f6', border: '#9ca3af', fg: '#4b5563' },
  };
  const c = colors[source];
  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.125rem 0.5rem',
    borderRadius: 999,
    background: c.bg,
    border: `1px solid ${c.border}`,
    color: c.fg,
    fontSize: '0.6875rem',
    fontWeight: 600,
    fontFamily: "'JetBrains Mono', Consolas, monospace",
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
  };
}

function countBadgeStyle(): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.125rem 0.5rem',
    borderRadius: 999,
    background: '#f3f4f6',
    border: '1px solid #d1d5db',
    color: '#4b5563',
    fontSize: '0.6875rem',
    fontWeight: 600,
    fontFamily: "'JetBrains Mono', Consolas, monospace",
  };
}

function chipStyle(): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.25rem 0.625rem',
    borderRadius: 999,
    background: '#ffffff',
    border: '1px solid #d1d5db',
    color: '#374151',
    fontSize: '0.75rem',
    fontWeight: 500,
  };
}

const sectionHeadingStyle: React.CSSProperties = {
  margin: '0 0 0.5rem 0',
  fontSize: '0.9375rem',
  fontWeight: 700,
  color: '#111827',
};

const proseStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '0.875rem',
  color: '#374151',
  lineHeight: 1.55,
};

const helpTextStyle: React.CSSProperties = {
  margin: '0.25rem 0 0 0',
  fontSize: '0.75rem',
  color: '#6b7280',
};

const modalBackdropStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(17, 24, 39, 0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '1rem',
};

const modalCardStyle: React.CSSProperties = {
  background: '#ffffff',
  borderRadius: '0.5rem',
  padding: '1.25rem 1.5rem',
  width: '100%',
  maxWidth: 480,
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.25)',
  maxHeight: '90vh',
  overflowY: 'auto',
};

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: React.ReactNode }) {
  return (
    <label
      htmlFor={htmlFor}
      style={{
        display: 'block',
        fontSize: '0.8125rem',
        fontWeight: 600,
        color: '#374151',
        marginTop: '0.875rem',
        marginBottom: '0.25rem',
      }}
    >
      {children}
    </label>
  );
}

function FieldError({ children }: { children: React.ReactNode }) {
  return (
    <p role="alert" style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#b91c1c' }}>
      {children}
    </p>
  );
}
